// composables
import { ref, nextTick, type Ref } from 'vue'
// composables
import useApiRequest from '@/composables/useApiRequest'

/**
 * Composable for managing agreement conversations
 * Handles fetching conversations and auto-scrolling to bottom
 */
export default function useAgreementConversations(agreementID: Ref<string | number>) {
  // state
  const conversationsTabRef = ref<HTMLElement | null>(null)

  // api requests
  const { data: conversationsData, send: conversationsFetchData } = useApiRequest()

  /**
   * Scrolls conversations to bottom
   */
  function scrollConversationsToBottom(): void {
    try {
      const root = conversationsTabRef.value
      if (!root) return

      // Wait for the component to be visible and DOM to be ready
      nextTick(() => {
        // Use requestAnimationFrame to ensure the element is visible
        requestAnimationFrame(() => {
          // Try to find the scroll container with retry logic
          const findAndScroll = (attempts = 0) => {
            const scroller = root.querySelector('.overflow-y-scroll') as HTMLElement
            if (!scroller && attempts < 5) {
              // Retry if element not found (component might still be mounting)
              setTimeout(() => findAndScroll(attempts + 1), 50)
              return
            }
            if (!scroller) return

            // Ensure element is visible before scrolling
            const isVisible = scroller.offsetHeight > 0 && scroller.offsetWidth > 0
            if (!isVisible && attempts < 5) {
              setTimeout(() => findAndScroll(attempts + 1), 50)
              return
            }

            // Scroll to bottom
            scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight
          }
          findAndScroll()
        })
      })
    } catch (err) {
      console.error('useAgreementConversations: Error scrolling conversations to bottom', err)
    }
  }

  /**
   * Fetches conversations for the agreement
   */
  function fetchConversations(): void {
    conversationsFetchData({ endpoint: '/v1/agreements/' + agreementID.value + '/conversations' })
  }

  /**
   * Handles when a new conversation is submitted
   */
  function newConversationSubmitted(): void {
    conversationsFetchData({ endpoint: '/v1/agreements/' + agreementID.value + '/conversations' })
  }

  return {
    // state
    conversationsData,
    conversationsTabRef,
    // methods
    fetchConversations,
    newConversationSubmitted,
    scrollConversationsToBottom
  }
}
